import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

interface RegisteredSearchContext {
  entityType?: string;
  vesselId?: string;
}

interface SearchContextValue {
  entityType: string | undefined;
  vesselId: string | undefined;
  registerContext: (ctx: RegisteredSearchContext) => void;
  resetContext: () => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export const useSearchContext = (): SearchContextValue => {
  const context = use(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within SearchContextProvider');
  }
  return context;
};

interface SearchContextProviderProps {
  children: React.ReactNode;
}

export const SearchContextProvider = ({ children }: SearchContextProviderProps) => {
  const [value, setValue] = useState<RegisteredSearchContext>(() => ({}));

  const registerContext = useCallback((ctx: RegisteredSearchContext) => {
    setValue({ entityType: ctx.entityType, vesselId: ctx.vesselId });
  }, []);

  const resetContext = useCallback(() => {
    setValue({});
  }, []);

  const contextValue = useMemo(
    () => ({
      entityType: value.entityType,
      vesselId: value.vesselId,
      registerContext,
      resetContext
    }),
    [value.entityType, value.vesselId, registerContext, resetContext]
  );

  return <SearchContext value={contextValue}>{children}</SearchContext>;
};

export const useRegisterSearchContext = (ctx: RegisteredSearchContext): void => {
  const { registerContext, resetContext } = useSearchContext();
  const { entityType, vesselId } = ctx;

  useEffect(() => {
    registerContext({ entityType, vesselId });
    return () => {
      resetContext();
    };
  }, [entityType, vesselId, registerContext, resetContext]);
};
